import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import statusCalculate from 'altrac-application-status'
import { FormattedMessage } from 'react-intl'
import Spinner from '../../util/spinner-shim'
import { useParams, useNavigate } from 'react-router-dom'

import AppDispatcher from '../../dispatchers/AppDispatcher'
import withAppStore from '../../util/withAppStore'

// internal libs
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import ConfigureButtons from '../partials/ConfigurePageComponents/ConfigureButtons'
import TelemetryButtons from '../partials/ConfigurePageComponents/TelemetryButtons'
import PhysicalButtons from '../partials/ConfigurePageComponents/PhysicalButtons'
import GeoLocated from '../partials/GeoLocated'

import styles from '../../../css/devicePage.module.css'

function DeviceAdmin (props) {
  const { customerID, deviceID } = useParams()
  const navigate = useNavigate()

  const [statusUpdateOnce, setStatusUpdateOnce] = React.useState(0)

  const getNewData = React.useCallback(() => {
    AppDispatcher.dispatch({ action: 'get-device', deviceID })
  }, [deviceID])

  const backToSettings = React.useCallback(() => {
    navigate(`/${customerID}/devices/${deviceID}/settings`)
  }, [customerID, deviceID, navigate])

  React.useEffect(() => {
    getNewData()
  }, [getNewData])

  React.useEffect(() => {
    const interval = setInterval(getNewData, props.data?.pollInterval || 5000)
    return () => clearInterval(interval)
  }, [getNewData, props.data?.pollInterval])

  React.useEffect(() => {
    return () => {
      AppDispatcher.dispatch({ action: 'unload-device-status' })
    }
  }, [])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}

    if (
      lookupReady &&
      lookup[deviceID] &&
      statusUpdateOnce === 0
    ) {
      setStatusUpdateOnce(1)
      const status = statusCalculate(lookup[deviceID])
      AppDispatcher.dispatch({ action: 'load-device-status', status })
    }
  }, [props.data?.lookupReady, props.data?.lookup, deviceID, statusUpdateOnce])

  const {
    auth: {
      logout,
    },
    data,
    data: {
      deviceStatus,
      lookup,
      lookupReady,
    },
    route: {
      path,
    },
    level,
  } = props

  let subject

  if (!lookupReady || !lookup[deviceID]) {
    subject = (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  } else {
    const device = lookup[deviceID]

    const {
      customer_id: customerIDFromDevice,
      physical: {
        deviceType,
      },
    } = device

    const basePath = `/${customerIDFromDevice}/devices/${deviceID}/`
    const settingPath = `${basePath}settings/`
    const dataPath = `${basePath}telemetry/`

    const admin = {
      telemetryTypes: [
        {
          unit: '%',
          label: 'Internal Battery',
          formula: 'default',
          valueKey: '129',
          multiplier: 0.01,
        },
        {
          unit: '',
          label: 'RSSI dB',
          formula: 'cellSignalToRssi',
          valueKey: '179',
          multiplier: 1,
        },
        'diagnosticsRun',
      ],
      settingTypes: [
        'sleepInterval',
        'update',
        'application',
      ],
      physicalTypes: deviceType === 'moistureSensor' || deviceType === 'temperature'
        ? [
            'deviceType',
          ]
        : [
            'deviceType',
          ],
    }
    const env = import.meta.env.VITE_APP_ENV

    subject = (
      <div className={styles.pageWidth}>
        <div>
          <div className={styles.pageContainer}>
            <div>
              <div className={styles.sectionName}>
                <FormattedMessage
                  id='admin.info.header'
                  defaultMessage='Info'
                  description='Info Label'
                  tagName='h2'
                />
              </div>
              {level >= 30
                ? (
                  <a
                    className={styles.settingButton}
                    href={`${env === 'production' ? 'https://admin.altrac.io' : 'https://admin.altrac-stage.com'}/devices/${deviceID}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <h3>
                      <i className='fa fa-external-link' />
                      &nbsp;Admin Portal
                    </h3>
                  </a>
                  )
                : ''}
              <PhysicalButtons
                device={device}
                path={settingPath}
                physicalTypes={admin.physicalTypes}
              />
              <div className={styles.sectionName}>
                <FormattedMessage
                  id='admin.telemetry.header'
                  defaultMessage='Telemetry'
                  description='Telemetry Label'
                  tagName='h2'
                />
              </div>
              <TelemetryButtons
                device={device}
                status={data.deviceStatus}
                path={basePath}
                telemetryTypes={admin.telemetryTypes}
                customer={data.customer.data}
                pinCode={data.pinCode}
                tempConv={data.user?.locale?.tempConv || 'f'}
                level={level}
              />
              <div className={styles.sectionName}>
                <FormattedMessage
                  id='admin.settings.header'
                  defaultMessage='Settings'
                  description='Settings Label'
                  tagName='h2'
                />
              </div>
              <ConfigureButtons
                applicationSettings={device.application_settings || { settings: {} }}
                applicationSettingsNew={device.application_settings_new || { settings: {} }}
                reading0={device.reading0 || {}}
                physical={device.physical || {}}
                interfaceVersioned={device.interface_versioned || {}}
                customer={data.customer.data}
                pinCode={data.pinCode}
                path={settingPath}
                settingTypes={admin.settingTypes}
              />
            </div>
            <div>
              <div className={styles.sectionName}>
                <FormattedMessage
                  id='admin.status.header'
                  defaultMessage='JSON Raw Status'
                  description='JSON Raw Status Label'
                  tagName='h2'
                />
              </div>
              <div className={styles.sectionName}>
                <pre>
                  {JSON.stringify(deviceStatus, null, 2)}
                </pre>
              </div>
            </div>
          </div>
          <div className={styles.sectionName}>
            <FormattedMessage
              id='admin.location.header'
              defaultMessage='Location'
              description='Location Label'
              tagName='h2'
            />
          </div>
          <div className={styles.sectionName}>
            <FormattedMessage
              id='admin.location.browser'
              defaultMessage='Browser Location'
              description='Browser Location Label'
              tagName='h4'
            />
            <GeoLocated />
          </div>
          <div className={styles.sectionName}>
            <FormattedMessage
              id='admin.location.device'
              defaultMessage='Device Location'
              description='Device Location Label'
              tagName='h4'
            />
            Nothing here yet!!!
          </div>
        </div>
        <br />
        <br />
        <br />
        <br />

      </div>
    )
  }

  const name = lookupReady ? lookup[deviceID].address_alias : ''

  return (
    <div>
      <Helmet>
        <title>Device Admin</title>
      </Helmet>
      <NavigationBar
        backFunc={backToSettings}
        name={name}
        page={path}
        logOut={() => logout(navigate)}
      />
      {subject}
      <Footer />
    </div>
  )
}

DeviceAdmin.propTypes = {
  data: PropTypes.object,
  route: PropTypes.object,
  level: PropTypes.number,
  auth: PropTypes.object,
}

export default withAppStore(DeviceAdmin)
